import React, { ReactNode, Suspense, useEffect, useRef } from 'react';
import { Navigate } from 'react-router-dom';

import { useAuth } from '../contexts/AuthContext';
import { useAuthPrompt } from '../contexts/AuthPromptContext';
import { canAccessAdmin } from '../utils/auth';

const ADMIN_TWO_FACTOR_PATH = '/admin/two-factor';

const DiscoverPage = React.lazy(() => import('../pages/DiscoverPage'));

interface ProtectedRouteProps {
  children: ReactNode;
  requireAdmin?: boolean;
  requireAuth?: boolean;
  /** The two-factor setup page itself, which an admin who must enrol has to reach. */
  allowWithoutTwoFactor?: boolean;
}

/** The home page with the sign-in pop-up held open over it, on the protected page's own URL. */
const SignInWall: React.FC = () => {
  const { holdForProtectedPage } = useAuthPrompt();
  useEffect(() => holdForProtectedPage(), [holdForProtectedPage]);
  return (
    <Suspense fallback={null}>
      <DiscoverPage />
    </Suspense>
  );
};

const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requireAdmin = false,
  requireAuth = true,
  allowWithoutTwoFactor = false,
}) => {
  const { isAuthenticated, user, isLoading, adminMfa } = useAuth();
  // True from the first render of the wall until the protected content next renders, so a
  // sign-in in flight keeps the wall instead of swapping it for the bootstrap placeholder.
  const hasShownWallRef = useRef(false);

  const isSignedOut = requireAuth && !isAuthenticated;
  const showsWall = isLoading ? hasShownWallRef.current : isSignedOut;
  const showsContent = !isLoading && !showsWall;

  useEffect(() => {
    if (showsWall) hasShownWallRef.current = true;
    else if (showsContent) hasShownWallRef.current = false;
  }, [showsWall, showsContent]);

  if (showsWall) return <SignInWall />;

  if (isLoading) {
    return (
      <div
        style={{
          maxWidth: '1024px',
          margin: '32px auto',
          padding: '0 16px',
          display: 'flex',
          justifyContent: 'center',
        }}
      >
        <span>Loading...</span>
      </div>
    );
  }

  if (requireAdmin && !canAccessAdmin(user)) {
    return (
      <div style={{ maxWidth: '1024px', margin: '32px auto', padding: '0 16px' }}>
        <div
          className="alert alert-error"
          style={{ flexDirection: 'column', alignItems: 'flex-start', padding: '24px' }}
        >
          <strong style={{ fontSize: '1.1rem', marginBottom: '8px' }}>Access Denied</strong>
          <p>
            You don't have permission to access this page. Administrator privileges are required.
          </p>
        </div>
      </div>
    );
  }

  if (requireAdmin && !allowWithoutTwoFactor && adminMfa?.required) {
    const mustEnrol = !adminMfa.enrolled || adminMfa.sign_in_method === 'federated';
    if (mustEnrol) return <Navigate replace to={ADMIN_TWO_FACTOR_PATH} />;
  }

  return <>{children}</>;
};

export default ProtectedRoute;
